import type { PDFDocumentProxy } from "pdfjs-dist";

let printFrame: { frame: HTMLIFrameElement; url: string } | undefined;

const removePrintFrame = () => {
	if (!printFrame) return;
	printFrame.frame.remove();
	URL.revokeObjectURL(printFrame.url);
	printFrame = undefined;
};

/**
 * Opens the file in a new tab, reporting whether the browser allowed it. `noopener` would make
 * `window.open` return null even when the tab opens, so the opener is cut by hand.
 */
const openPrintTab = (url: string) => {
	const tab = window.open(url, "_blank");
	if (tab) tab.opener = null;
	return tab !== null;
};

/**
 * Prints the original file, not the rendered pages, from a hidden same-origin frame. Where the
 * browser won't print a PDF from a frame (Firefox), the file opens in a new tab to print from
 * there. That runs after the frame loads, outside the click, so a pop-up blocker may stop it;
 * the promise then rejects so the viewer can say so.
 */
export const printDocument = async (pdf: Pick<PDFDocumentProxy, "getData">) => {
	const data = await pdf.getData();
	removePrintFrame();
	const url = URL.createObjectURL(new Blob([data.slice()], { type: "application/pdf" }));
	const frame = document.createElement("iframe");
	frame.title = "Print document";
	frame.setAttribute("aria-hidden", "true");
	frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0";
	printFrame = { frame, url };
	await new Promise<void>((resolve, reject) => {
		frame.addEventListener(
			"load",
			() => {
				try {
					frame.contentWindow?.focus();
					frame.contentWindow?.print();
					resolve();
				} catch {
					if (openPrintTab(url)) resolve();
					else reject(new Error("The browser blocked the tab to print from."));
				}
			},
			{ once: true },
		);
		frame.src = url;
		document.body.append(frame);
	});
};
